import { CSSProperties, useMemo, useState } from "react"
import { KpiRow } from "models/financial-kpi"
import { ChevronDownIcon, ChevronRightIcon } from "Components/atoms/icons"
import { FISCAL_MONTHS, formatKpiValue, formatPct } from "./format"

interface KpiTableProps {
    rows: KpiRow[]
    // Rows that start collapsed (by key); children of a collapsed row are hidden.
    defaultCollapsed?: string[]
    emptyMessage?: string
}

const NAME_WIDTH = 260

const stickyName: CSSProperties = {
    position: "sticky", left: 0, zIndex: 1, minWidth: NAME_WIDTH, maxWidth: NAME_WIDTH,
    background: "var(--bs-body-bg, #fff)",
}
const numberCell: CSSProperties = { textAlign: "right", whiteSpace: "nowrap", minWidth: 105 }

// Name | Position | Owner | Target | Sep … Aug | YTD | % of target.
// A row is a parent when the next row is deeper; parents can be collapsed.
// Month cells taken from a saved snapshot are marked with an "S" badge, live values with "L".
const KpiTable = ({ rows, defaultCollapsed = [], emptyMessage = "No data for this fiscal year." }: KpiTableProps) => {
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set(defaultCollapsed))

    const isParent = (i: number) => i + 1 < rows.length && rows[i + 1].depth > rows[i].depth

    // Hide every row under a collapsed ancestor
    const visible = useMemo(() => {
        const out: { row: KpiRow, index: number }[] = []
        let hideBelow: number | null = null
        rows.forEach((row, index) => {
            if (hideBelow !== null && row.depth > hideBelow) return
            hideBelow = null
            out.push({ row, index })
            if (collapsed.has(row.key) && isParent(index)) hideBelow = row.depth
        })
        return out
    }, [rows, collapsed])

    const toggle = (key: string) => setCollapsed(prev => {
        const next = new Set(prev)
        next.has(key) ? next.delete(key) : next.add(key)
        return next
    })

    if (rows.length === 0) return <div className="text-muted text-center py-3">{emptyMessage}</div>

    return (
        <div style={{ overflowX: "auto" }}>
            <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                <thead>
                    <tr>
                        <th style={stickyName}>KPI</th>
                        <th style={{ minWidth: 110 }}>Position</th>
                        <th style={{ minWidth: 140 }}>Owner</th>
                        <th style={numberCell}>Target</th>
                        {FISCAL_MONTHS.map(m => <th key={m} style={numberCell}>{m}</th>)}
                        <th style={numberCell}>YTD</th>
                        <th style={{ ...numberCell, minWidth: 70 }}>% Target</th>
                    </tr>
                </thead>
                <tbody>
                    {visible.map(({ row, index }) => {
                        const parent = isParent(index)
                        const bold = row.depth === 0 ? "fw-semibold" : ""
                        return (
                            <tr key={row.key}>
                                <td style={{ ...stickyName, paddingLeft: 8 + row.depth * 18 }} className={bold}>
                                    <span className="d-inline-flex align-items-center gap-1" style={{ cursor: parent ? "pointer" : undefined }}
                                        onClick={parent ? () => toggle(row.key) : undefined}>
                                        {parent
                                            ? (collapsed.has(row.key) ? <ChevronRightIcon /> : <ChevronDownIcon />)
                                            : <span style={{ display: "inline-block", width: 16 }} />}
                                        {row.label}
                                    </span>
                                </td>
                                <td>{row.position ?? <span className="text-muted">—</span>}</td>
                                <td>{row.owner ?? <span className="text-muted">—</span>}</td>
                                <td style={numberCell} className={bold}>{formatKpiValue(row.target, row.unit)}</td>
                                {row.months.map((value, m) => {
                                    const source = row.sources?.[m]
                                    return (
                                        <td key={m} style={numberCell} className={value !== null && value < 0 ? "text-danger" : undefined}
                                            title={source === "snapshot" ? "Saved snapshot" : source === "live" ? "Live (not saved yet)" : undefined}>
                                            {formatKpiValue(value, row.unit)}
                                            {value !== null && source && source !== "actual" &&
                                                <sup className="ms-1 text-muted">{source === "snapshot" ? "S" : "L"}</sup>}
                                        </td>
                                    )
                                })}
                                <td style={numberCell} className={bold}>{formatKpiValue(row.ytd, row.unit)}</td>
                                <td style={numberCell}>{formatPct(row.pct)}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default KpiTable
